import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import TabNavigation from "./ui/TabNavigation";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import {
  CogIcon,
  LinkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  PlayIcon,
  ClockIcon,
  ArrowPathIcon,
  PlusIcon,
  EyeIcon,
  DocumentTextIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import toast from "../utils/notify";

const ExternalIntegrations = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboardData, setDashboardData] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [integrationTypes, setIntegrationTypes] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const { token } = useAuth();

  // Fetch data on component mount
  useEffect(() => {
    const fetchIntegrationData = async () => {
      if (!token) return;
      
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Fetch dashboard data
        const dashboardRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/dashboard`, { headers });
        setDashboardData(dashboardRes.data);
        
        // Fetch integrations
        const integrationsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/integrations`, { headers });
        setIntegrations(integrationsRes.data);
        
        // Fetch integration types
        const typesRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/types`, { headers });
        setIntegrationTypes(typesRes.data);
        
        // Fetch health data
        const healthRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/health`, { headers });
        setHealthData(healthRes.data);
        
      } catch (error) {
        console.error('Error fetching integration data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrationData();
  }, [token]);

  const testConnection = async (integrationId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/${integrationId}/test`, {}, { headers });
      (response.data.test_result.is_healthy ? toast.success : toast.error)(`Connection test ${response.data.test_result.is_healthy ? 'successful' : 'failed'}`);
    } catch (error) {
      console.error('Error testing connection:', error);
      toast.error('Connection test failed');
    }
  };

  const triggerSync = async (integrationId, mappingType = 'compliance_frameworks') => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/${integrationId}/sync`, {
        mapping_type: mappingType,
        direction: 'bidirectional'
      }, { headers });
      
      toast.success(`Sync triggered successfully! ${response.data.summary.records_successful} records synced.`);
      
      // Refresh data
      const integrationsRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/integrations`, { headers });
      setIntegrations(integrationsRes.data);
      
    } catch (error) {
      console.error('Error triggering sync:', error);
      toast.error('Sync failed');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
      case "error":
        return <XCircleIcon className="w-5 h-5 text-red-600" />;
      case "syncing":
        return <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />;
      case "pending":
        return <ClockIcon className="w-5 h-5 text-yellow-600" />;
      default:
        return <ExclamationTriangleIcon className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "connected":
      case "healthy":
        return "bg-green-100 text-green-800";
      case "error":
        return "bg-red-100 text-red-800";
      case "syncing":
        return "bg-blue-100 text-blue-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "pending":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString() + " " + new Date(dateString).toLocaleTimeString();
  };

  const tabs = [
    { id: "dashboard", name: "Integration Dashboard", icon: ChartBarIcon },
    { id: "integrations", name: "Active Integrations", icon: LinkIcon },
    { id: "catalog", name: "Integration Catalog", icon: CogIcon },
    { id: "health", name: "Health Monitoring", icon: EyeIcon },
  ];

  const renderDashboardTab = () => (
    <div className="space-y-8">
      {/* Overview Metrics */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(dashboardData.overview).map(([key, value], index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600 mb-2">
                  {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </p>
                <p className="text-3xl font-bold text-gray-900">{value}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Popular Integrations */}
      {dashboardData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-6">Popular Integration Platforms</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {dashboardData.popular_integrations.map((integration, index) => (
              <div key={integration.type} className="text-center p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-900 mb-2">
                  {integration.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h4>
                <div className="text-2xl font-bold text-blue-600 mb-1">{integration.count}</div>
                <div className="text-sm text-gray-600">{integration.usage_percentage}% usage</div>
                <div className="mt-2">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${integration.usage_percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Sync Activities */}
      {dashboardData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-6">Recent Sync Activities</h3>
          <div className="space-y-4">
            {dashboardData.recent_sync_activities.map((activity, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {getStatusIcon(activity.status)}
                  <div>
                    <h4 className="font-medium text-gray-900">{activity.integration_name}</h4>
                    <p className="text-sm text-gray-600">{activity.sync_type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {activity.records_synced} records
                  </div>
                  <div className="text-xs text-gray-500">{formatDate(activity.timestamp)}</div>
                  {activity.error && (
                    <div className="text-xs text-red-600 mt-1">{activity.error}</div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );

  const renderIntegrationsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold text-gray-900">Active Integrations</h3>
        <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
          <PlusIcon className="w-4 h-4" />
          <span>Add Integration</span>
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrations.map((integration, index) => (
          <motion.div
            key={integration.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <LinkIcon className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900">{integration.name}</h4>
              </div>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(integration.status)}`}>
                {integration.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">{integration.description}</p>
            
            <div className="space-y-2 mb-4">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Type:</span>
                <span className="font-medium">{integration.integration_type.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Sync Direction:</span>
                <span className="font-medium">{integration.sync_direction.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Last Sync:</span>
                <span className="font-medium">
                  {integration.last_sync ? formatDate(integration.last_sync) : 'Never'}
                </span>
              </div>
            </div>
            
            {integration.error_message && (
              <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                {integration.error_message}
              </div>
            )}
            
            <div className="flex space-x-2">
              <button 
                onClick={() => testConnection(integration.id)}
                className="flex-1 bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center justify-center space-x-1"
              >
                <PlayIcon className="w-4 h-4" />
                <span>Test</span>
              </button>
              <button 
                onClick={() => triggerSync(integration.id)}
                className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center justify-center space-x-1"
              >
                <ArrowPathIcon className="w-4 h-4" />
                <span>Sync</span>
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderCatalogTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">Integration Catalog</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {integrationTypes.map((type, index) => (
          <motion.div
            key={type.type}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-lg shadow p-6"
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <CogIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">{type.name}</h4>
                <p className="text-sm text-gray-600">{type.category}</p>
              </div>
            </div>
            
            <p className="text-sm text-gray-700 mb-4">{type.description}</p>
            
            <div className="mb-4">
              <span className="text-sm font-medium text-gray-700">Supported Data Types:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {type.supported_data_types.map((dataType, idx) => (
                  <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                    {dataType.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <span className="text-sm font-medium text-gray-700">Authentication:</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {type.authentication_methods.map((method, idx) => (
                  <span key={idx} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded">
                    {method.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
            
            <div className="flex space-x-2">
              <button className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm">
                Configure
              </button>
              {type.documentation_url && (
                <button className="flex-1 bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center justify-center space-x-1">
                  <DocumentTextIcon className="w-4 h-4" />
                  <span>Docs</span>
                </button>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );

  const renderHealthTab = () => (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-gray-900">Integration Health Monitoring</h3>
      
      {healthData && (
        <>
          {/* Health Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="text-center">
                <CheckCircleIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-600">Healthy</p>
                <p className="text-3xl font-bold text-green-600">{healthData.healthy_count}</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="text-center">
                <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-600">Warning</p>
                <p className="text-3xl font-bold text-yellow-600">{healthData.warning_count}</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="text-center">
                <XCircleIcon className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-600">Error</p>
                <p className="text-3xl font-bold text-red-600">{healthData.error_count}</p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="text-center">
                <ClockIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-3xl font-bold text-blue-600">{healthData.total_integrations}</p>
              </div>
            </motion.div>
          </div>
          
          {/* Individual Integration Health */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Integration Status Details</h4>
            <div className="space-y-4">
              {healthData.integration_status.map((integration, index) => (
                <div key={integration.integration_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getStatusIcon(integration.status)}
                    <div>
                      <h5 className="font-medium text-gray-900">{integration.name}</h5>
                      <p className="text-sm text-gray-600">Last checked: {formatDate(integration.last_check)}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {integration.response_time_ms && (
                      <div className="text-sm font-medium text-gray-900">
                        {integration.response_time_ms}ms
                      </div>
                    )}
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(integration.status)}`}>
                      {integration.status.toUpperCase()}
                    </span>
                    {integration.error && (
                      <div className="text-xs text-red-600 mt-1">{integration.error}</div>
                    )}
                    {integration.warning && (
                      <div className="text-xs text-yellow-600 mt-1">{integration.warning}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </>
      )}
    </div>
  );

  return (
      <div className="p-8">
        {/* Header */}
        <div className="mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-3xl font-bold text-gray-900">External Compliance Tools Integration</h1>
            <p className="text-gray-600 mt-2">
              Connect and synchronize with popular compliance platforms for seamless data exchange and unified governance
            </p>
          </motion.div>
        </div>

        {/* Tab Navigation */}
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {/* Tab Content */}
        <div className="tab-content">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {activeTab === "dashboard" && renderDashboardTab()}
              {activeTab === "integrations" && renderIntegrationsTab()}
              {activeTab === "catalog" && renderCatalogTab()}
              {activeTab === "health" && renderHealthTab()}
            </>
          )}
        </div>
      </div>
  );
};

export default ExternalIntegrations;